/* Landing behaviour: gallery, one-step checkout, order bump total, sticky bar, reveal. */
(() => {
  const formatMoney = (cents) => {
    const format = window.bkMoneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const group = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(',');
        case 'amount_with_comma_separator': return `${group('.')},${decimals}`;
        case 'amount_no_decimals_with_comma_separator': return group('.');
        case 'amount_with_apostrophe_separator': return `${group("'")}.${decimals}`;
        default: return `${group(',')}.${decimals}`;
      }
    });
  };

  /* Gallery: scroll-snap track with arrows and dots. */
  document.querySelectorAll('[data-bk-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('.bk-gallery__track');
    const slides = [...track.children];
    const nav = gallery.querySelector('.bk-gallery__nav');
    const dotsWrap = gallery.querySelector('.bk-gallery__dots');
    if (slides.length < 2) return;
    nav.hidden = false;

    const dots = slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bk-gallery__dot';
      dot.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
      dot.addEventListener('click', () => go(index));
      dotsWrap.append(dot);
      return dot;
    });

    let current = 0;
    let moving = false;
    const show = () => dots.forEach((dot, index) => dot.setAttribute('aria-current', index === current ? 'true' : 'false'));
    // Scroll snapping fights programmatic smooth scrolls in Chromium, so it is off while a move animates.
    const go = (index) => {
      current = (index + slides.length) % slides.length;
      moving = true;
      show();
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: current * track.clientWidth, behavior: 'smooth' });
      window.clearTimeout(go.timer);
      go.timer = window.setTimeout(() => {
        track.scrollLeft = current * track.clientWidth;
        track.style.scrollSnapType = '';
        moving = false;
      }, 650);
    };
    // Swipes and trackpad scrolls move the dots; clicks set them straight away.
    track.addEventListener('scroll', () => window.requestAnimationFrame(() => {
      if (moving) return;
      current = Math.round(track.scrollLeft / track.clientWidth);
      show();
    }), { passive: true });
    gallery.querySelector('[data-prev]').addEventListener('click', () => go(current - 1));
    gallery.querySelector('[data-next]').addEventListener('click', () => go(current + 1));

    // Mouse: drag to change photo, or click the photo to see the next one.
    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false };
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (Math.abs(dx) > 4) drag.moved = true;
      track.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const { moved } = drag;
      drag = null;
      track.classList.remove('is-dragging');
      if (!moved) go(current + 1);
      else if (Math.abs(dx) > track.clientWidth * 0.12) go(current + (dx < 0 ? 1 : -1));
      else go(current);
    });
    track.addEventListener('dragstart', (event) => event.preventDefault());

    gallery.tabIndex = 0;
    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') go(current + 1);
      if (event.key === 'ArrowLeft') go(current - 1);
    });
    show();
  });

  /* Gift card: fields only count while the box is ticked; the preview follows what is typed. */
  const gift = document.querySelector('[data-bk-gift]');
  if (gift) {
    const toggle = gift.querySelector('[data-bk-gift-toggle]');
    const body = gift.querySelector('[data-bk-gift-body]');
    const fields = [...gift.querySelectorAll('[data-bk-gift-field]')];
    const count = gift.querySelector('[data-bk-gift-count]');
    const outputs = {};
    gift.querySelectorAll('[data-bk-gift-out]').forEach((node) => {
      outputs[node.dataset.bkGiftOut] = { node, example: node.textContent };
    });

    toggle.addEventListener('change', () => {
      body.hidden = !toggle.checked;
      fields.forEach((field) => { field.disabled = !toggle.checked; });
      if (toggle.checked) fields[0].focus();
    });
    fields.forEach((field) => field.addEventListener('input', () => {
      const out = outputs[field.dataset.bkGiftField];
      if (out) out.node.textContent = field.value.trim() || out.example;
      if (field.tagName === 'TEXTAREA' && count) count.textContent = `${field.value.length}/${field.maxLength}`;
    }));
  }

  /* Purchase: the bump checkbox and every buy button belong to form#bk-buy. */
  const form = document.getElementById('bk-buy');
  if (form) {
    const bumps = () => [...document.querySelectorAll('input[data-bk-bump][form="bk-buy"]')];
    const basePrice = Number(form.dataset.price) || 0;

    const updateTotals = () => {
      const total = bumps().reduce((sum, input) => sum + (input.checked ? Number(input.dataset.price) || 0 : 0), basePrice);
      document.querySelectorAll('[data-bk-total]').forEach((node) => { node.textContent = formatMoney(total); });
    };
    bumps().forEach((input) => input.addEventListener('change', () => {
      bumps().forEach((other) => { if (other !== input && other.value === input.value) other.checked = input.checked; });
      updateTotals();
    }));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      /* Payment platform as seller: keep the visit's tracking parameters (utm_*, fbclid…) on the way out. */
      if (window.bkExternalCheckout) {
        const target = new URL(window.bkExternalCheckout, window.location.href);
        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (!target.searchParams.has(key)) target.searchParams.set(key, value);
        });
        window.location.href = target.toString();
        return;
      }

      /* Required before paying: the digital-content consent, and the gift fields while the gift box is ticked. */
      const consent = document.querySelector('[data-bk-consent]');
      const giftFields = [...document.querySelectorAll('[data-bk-gift-field]:not(:disabled)')];
      const missing = [consent, ...giftFields].find((field) => field && !field.checkValidity());
      if (missing) {
        missing.reportValidity();
        return;
      }

      const properties = {};
      giftFields.forEach((field) => {
        const key = field.name.match(/\[properties\]\[(.+)\]$/)[1];
        if (field.value.trim()) properties[key] = field.value.trim();
      });
      const buttons = document.querySelectorAll('[form="bk-buy"][type="submit"]');
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));

      const items = [{ id: Number(form.querySelector('[name="items[0][id]"]').value), quantity: 1, properties }];
      const seen = new Set();
      bumps().forEach((input) => {
        if (input.checked && !seen.has(input.value)) {
          seen.add(input.value);
          items.push({ id: Number(input.value), quantity: 1 });
        }
      });

      try {
        const root = window.Shopify?.routes?.root || '/';
        await fetch(`${root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error(`cart/add ${response.status}`);
        const implicit = document.querySelector('[data-bk-consent-implicit]');
        if (consent || implicit) {
          const how = consent ? 'Sí' : 'Sí, al pulsar el botón de compra';
          await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { 'Entrega inmediata aceptada': `${how} · ${new Date().toISOString()}` } }),
          });
        }
        window.location.href = form.dataset.checkout;
      } catch (error) {
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
        HTMLFormElement.prototype.submit.call(form);
      }
    });

    /* Sticky bar shows whenever the main buy button is off screen: below it on phones at first, above it after scrolling. */
    const sticky = document.querySelector('[data-bk-sticky]');
    const anchor = document.querySelector('[data-bk-anchor]');
    if (sticky && anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', passed);
        sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
      }).observe(anchor);
    } else if (sticky) {
      sticky.classList.add('is-visible');
    }
  }

  /* Gentle reveal for sections below the fold. */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('[data-bk-reveal]').forEach((node) => {
      if (node.getBoundingClientRect().top > window.innerHeight) {
        node.classList.add('bk-reveal');
        observer.observe(node);
      }
    });
  }
})();
